/**
 * Provides coin metadata and pricing data from AlphaLend API with automatic caching.
 */
import { normalizeStructTag } from '@mysten/sui/utils';
import { Decimal } from 'decimal.js';
import { SingletonCache } from '../utils/cache.js';
import { CACHE_TTL, URLS } from '../utils/constants.js';
export class CoinInfoProvider {
    apiUrl = URLS.COININFO_API;
    cache;
    constructor() {
        // 5 minute TTL for coin data
        this.cache = new SingletonCache(CACHE_TTL.COIN_INFO);
    }
    /** Get all coin metadata with caching and promise deduplication. */
    async getAllCoins() {
        return this.cache.getOrFetch(() => this.fetchFromApi());
    }
    /** Get coin metadata by symbol. Prefers exact case match; falls back to case-insensitive. */
    async getCoinBySymbol(symbol) {
        const coins = Array.from((await this.getAllCoins()).values());
        return (coins.find((coin) => coin.symbol === symbol) ??
            coins.find((coin) => coin.symbol.toUpperCase() === symbol.toUpperCase()));
    }
    /** Get coin metadata by type. */
    async getCoinByType(coinType) {
        const coins = await this.getAllCoins();
        return coins.get(normalizeStructTag(coinType));
    }
    /** Get coin price (Pyth preferred, fallback to CoinGecko). */
    async getPriceByType(coinType) {
        const coin = await this.getCoinByType(normalizeStructTag(coinType));
        if (!coin)
            throw new Error(`Coin not found: ${coinType}`);
        const price = coin.pythPrice ?? coin.coingeckoPrice;
        if (!price)
            throw new Error(`No price available for: ${coinType}`);
        return price;
    }
    /** Clear the cache to force fresh data on next request. */
    clear() {
        this.cache.clear();
    }
    /** Fetch coin data from AlphaLend GraphQL API. */
    async fetchFromApi() {
        const query = `
      query {
        coinInfo {
          coinType
          symbol
          decimals
          coingeckoPrice
          pythPrice
        }
      }
    `;
        const response = await fetch(this.apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query }),
        });
        if (!response.ok) {
            throw new Error(`AlphaLend API error: ${response.status}`);
        }
        const json = (await response.json());
        const apiCoins = json?.data?.coinInfo ?? [];
        const coins = new Map();
        apiCoins.forEach((apiCoin) => {
            coins.set(normalizeStructTag(apiCoin.coinType), {
                coinType: normalizeStructTag(apiCoin.coinType),
                symbol: apiCoin.symbol,
                decimals: apiCoin.decimals,
                coingeckoPrice: apiCoin.coingeckoPrice !== undefined && apiCoin.coingeckoPrice !== null
                    ? new Decimal(apiCoin.coingeckoPrice)
                    : undefined,
                pythPrice: apiCoin.pythPrice !== undefined && apiCoin.pythPrice !== null
                    ? new Decimal(apiCoin.pythPrice)
                    : undefined,
            });
        });
        return coins;
    }
}
//# sourceMappingURL=coinInfoProvider.js.map